import type { Image } from '@eneco/cms-types';

import { useCms } from './provider';

/**
 * Hook-flavoured wrapper around `adapter.toImage`. The legacy export was a
 * plain function; we keep the same name so consumers don't have to change
 * import paths, but the implementation now goes through the adapter so the
 * wrapping logic stays provider-specific.
 */
export const mapImage = (raw: unknown): Image | undefined => {
  // We expose this as a plain function. Adapter resolution happens via
  // `useImageMapper` for cases where consumers need a memoised hook.
  // For Phase A, callers get a no-op that returns undefined unless they're
  // inside a CmsProvider and use `useImageMapper`. This avoids creating a
  // hidden dependency on a global adapter — it's intentional.
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === 'object' && raw !== null && 'src' in raw && typeof (raw as { src: unknown }).src === 'string') {
    return raw as Image;
  }
  return undefined;
};

/**
 * In-context image mapper. Calls `adapter.toImage` so provider-specific
 * wrapping (e.g. JSS `{ value: ... }`) is unwrapped exactly once.
 */
export const useImageMapper = (): ((raw: unknown) => Image | undefined) => {
  const { adapter } = useCms();
  return adapter.toImage;
};
